'use client';
import React, { useState } from 'react';
import GoogleMapComponent from '@components/googleMap';
import { api } from '@utils/apiv2';
import PlacesAutocomplete from '@components/centers/PlacesAutocomplete';
import CenterListItem from '@components/centers/CenterListItem';
import { useQuery } from '@tanstack/react-query';
import { Loader } from '@components/loader';
import { APIProvider, useApiIsLoaded } from '@vis.gl/react-google-maps';

const PAGE_SIZE = 100;

async function fetchAllCenters(lat: number, lng: number) {
  const firstPage = await api.get({
    path: 'centers/list',
    param: { lat, lng, size: PAGE_SIZE, page: 1 },
  });

  const allData = [...(firstPage.data || [])];
  const totalPages = firstPage.meta?.pages || 1;

  if (totalPages > 1) {
    const remainingPages = Array.from(
      { length: totalPages - 1 },
      (_, i) => i + 2
    );
    const results = await Promise.all(
      remainingPages.map((page) =>
        api.get({
          path: 'centers/list',
          param: { lat, lng, size: PAGE_SIZE, page },
        })
      )
    );
    results.forEach((res) => allData.push(...(res.data || [])));
  }

  return allData;
}

function MapSectionInner({ initialLocation, initialCenters }) {
  const apiIsLoaded = useApiIsLoaded();
  const [location, setLocation] = useState({
    address: initialLocation?.locationName,
    latitude: initialLocation?.lat,
    longitude: initialLocation?.lng,
    isInputAllowed: true,
  });
  const { data: allCenters, isLoading } = useQuery({
    queryKey: ['allCenters', location.latitude, location.longitude],
    queryFn: async () => {
      const data = await fetchAllCenters(
        location.latitude || 40.73061,
        location.longitude || -73.935242
      );
      return data.filter(
        (center) => center.email || center.phone1 || center.phone2
      );
    },
    // Use placeholder data so we still fetch when queryKey changes.
    placeholderData: initialCenters,
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });
  return (
    <section className="map-section">
      {isLoading && <Loader />}
      <GoogleMapComponent allCenters={allCenters} />
      <div className="center-search-box" id="mobile-handler">
        <div className="mobile-handler"></div>
        {apiIsLoaded && (
          <PlacesAutocomplete
            initialLocation={initialLocation}
            location={location}
            setLocation={setLocation}
          />
        )}
        <div className="search-listing">
          {allCenters?.map((center) => {
            return <CenterListItem key={center.sfid} center={center} />;
          })}
        </div>
      </div>
    </section>
  );
}

export default function MapSection({ initialLocation, initialCenters }) {
  return (
    <APIProvider
      apiKey={process.env.NEXT_PUBLIC_GOOGLE_MAP_API_KEY || ''}
      libraries={['places']}
    >
      <MapSectionInner
        initialLocation={initialLocation}
        initialCenters={initialCenters}
      />
    </APIProvider>
  );
}
